/**
 * ClientHowConsultantsUse Component
 * Section 4: How Consultants Use OKR Hub
 */

import React from 'react';
import { Users, MessageSquare, TrendingUp, UserCheck } from 'lucide-react';

export const ClientHowConsultantsUse: React.FC = () => {
  const scrollToForm = () => {
    const element = document.getElementById('execution-fit-form');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const engagementStyles = [
    { icon: Users, label: 'Leadership workshops' },
    { icon: MessageSquare, label: 'Small-group alignment sessions' },
    { icon: UserCheck, label: 'One-on-one discussions with functional heads' },
    { icon: TrendingUp, label: 'Ongoing advisory or fractional roles' }
  ];

  const typicalFlow = [
    'You align leadership on priorities',
    'Outcomes are clarified',
    'Execution runs on OKR Hub',
    'Progress becomes visible and review-ready',
    'You step back without losing outcome integrity'
  ];

  const importantPoints = [
    'Works whether or not you explicitly call your framework "OKRs"',
    'Does not force templates or rigid processes',
    'Standardizes execution without standardizing thinking'
  ];

  return (
    <section className="bg-white py-20 md:py-32">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">

          {/* Headline */}
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-stone-900 mb-12 leading-tight">
            Designed to work with how you already consult.
          </h2>

          {/* Content */}
          <div className="space-y-10 text-lg md:text-xl text-stone-700 leading-relaxed">

            <p>
              OKR Hub fits naturally into different engagement styles:
            </p>

            {/* Engagement Styles Grid */}
            <div className="grid md:grid-cols-2 gap-4">
              {engagementStyles.map((style, index) => {
                const Icon = style.icon;
                return (
                  <div key={index} className="flex items-center gap-3 bg-stone-50 p-4 rounded-lg border border-stone-200">
                    <Icon className="w-5 h-5 text-stone-600 flex-shrink-0" />
                    <span className="text-base text-stone-900">{style.label}</span>
                  </div>
                );
              })}
            </div>

            <p className="pt-6 text-stone-900 font-medium">
              A typical flow looks like this:
            </p>

            {/* Typical Flow */}
            <div className="bg-stone-50 border-2 border-stone-200 rounded-xl p-8 space-y-4">
              {typicalFlow.map((step, index) => (
                <div key={index} className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-stone-900 text-white rounded-full flex items-center justify-center font-semibold text-sm">
                    {index + 1}
                  </div>
                  <p className="text-lg text-stone-900 pt-0.5">{step}</p>
                </div>
              ))}
            </div>

            <p className="pt-6 text-stone-900 font-medium">
              Important to note:
            </p>

            {/* Important Points */}
            <ul className="space-y-3 ml-6">
              {importantPoints.map((point, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-stone-400 mr-3">•</span>
                  <span>{point}</span>
                </li>
              ))}
            </ul>

          </div>

          {/* CTA */}
          <div className="mt-12">
            <button
              onClick={scrollToForm}
              className="inline-flex items-center justify-center px-6 py-3 text-base font-medium text-stone-900 border-2 border-stone-300 rounded-lg hover:border-stone-900 hover:bg-stone-50 transition-all duration-300"
            >
              Talk through a real client scenario
            </button>
          </div>

        </div>
      </div>
    </section>
  );
};
